import React, { useMemo } from 'react';

import { Box, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';

const BAR_COLORS = [
  '#A379E7',
  '#4FD1C5',
  '#F6AD55',
  '#63B3ED',
  '#FC8181',
];

interface RelatedBreakdownChartProps {
  title: string;
  data: Array<{ label: string; count: number }>;
  emptyLabel?: string;
}

/**
 * Proportional bar rows (not Recharts). Recharts vertical BarCharts mis-measure
 * and crash (minPointSize / layout) inside Collapse + lazy Tabs in this overlay.
 */
export const RelatedBreakdownChart: React.FC<RelatedBreakdownChartProps> = ({
  title,
  data,
  emptyLabel = 'No breakdown available',
}) => {
  const rows = useMemo(() => {
    if (!data.length) return [];

    const maxCount = Math.max(...data.map(item => item.count), 1);

    return data.map((item, index) => ({
      ...item,
      percent: Math.max(4, Math.round((item.count / maxCount) * 100)),
      color: BAR_COLORS[index % BAR_COLORS.length],
    }));
  }, [data]);

  return (
    <Box w='full' data-cy='related-works-chart'>
      <Text
        fontSize='xs'
        fontWeight='semibold'
        color='tabs.font-selected'
        textTransform='uppercase'
        letterSpacing='0.02em'
        mb={2}
      >
        {title}
      </Text>
      {rows.length === 0 ? (
        <Text fontSize='xs' opacity={0.5} color='fonts.primary'>
          {emptyLabel}
        </Text>
      ) : (
        <VStack align='stretch' gap={2.5} w='full'>
          {rows.map(row => (
            <Tooltip
              key={`${row.label}-${row.count}`}
              label={`${row.label} · ${row.count.toLocaleString()} jobs`}
              hasArrow
              openDelay={200}
              placement='top'
            >
              <Box w='full' minW={0}>
                <HStack justify='space-between' align='baseline' gap={3} mb={1}>
                  <Text
                    fontSize='xs'
                    color='fonts.primary'
                    noOfLines={1}
                    minW={0}
                    flex={1}
                  >
                    {row.label}
                  </Text>
                  <Text
                    fontSize='xs'
                    fontWeight='semibold'
                    color='fonts.primary'
                    opacity={0.75}
                    flexShrink={0}
                  >
                    {row.count.toLocaleString()}
                  </Text>
                </HStack>
                <Box
                  h='8px'
                  w='full'
                  rounded='full'
                  bg='glass-items.bg'
                  overflow='hidden'
                >
                  <Box
                    h='full'
                    w={`${row.percent}%`}
                    rounded='full'
                    bg={row.color}
                    transition='width 0.2s ease'
                  />
                </Box>
              </Box>
            </Tooltip>
          ))}
        </VStack>
      )}
    </Box>
  );
};
